/** @format */

import {styled, Theme} from '@mui/material/styles';
import {Box, Button, Paper, Typography} from '@mui/material';

export type ModalType = 'primary' | 'good' | 'bad' | 'alert';

interface TypeProps {
  customType: ModalType;
}

const getBgColor = (type: ModalType, theme: Theme): string | undefined => {
  switch (type) {
    case 'primary':
      return theme.palette.semantic_light?.primary.container;
    case 'bad':
      return theme.palette.semantic_light?.bad.container;
    case 'good':
      return theme.palette.semantic_light?.good.container;
    case 'alert':
      return theme.palette.semantic_light?.alert.container;
    default:
      return undefined;
  }
};

const getCloseIconColor = (type: ModalType, theme: Theme): string | undefined => {
  switch (type) {
    case 'primary':
      return theme.palette.semantic_light?.primary.main;
    case 'bad':
      return theme.palette.semantic_light?.bad.main;
    case 'good':
      return theme.palette.semantic_light?.good.main;
    case 'alert':
      return theme.palette.semantic_light?.alert.main;
    default:
      return undefined;
  }
};

const getOnContainerColor = (type: ModalType, theme: Theme): string | undefined => {
  switch (type) {
    case 'primary':
      return theme.palette.semantic_light?.primary.onContainer;
    case 'bad':
      return theme.palette.semantic_light?.bad.onContainer;
    case 'good':
      return theme.palette.semantic_light?.good.onContainer;
    case 'alert':
      return theme.palette.semantic_light?.alert.onContainer;
    default:
      return undefined;
  }
};

export const BgPaper = styled(Paper)(({theme}) => ({
  borderRadius: '8px',
  overflow: 'hidden',
  width: 'max-content',
  maxWidth: `calc(100vw - ${theme.spacing(8)})`,
  outline: 'none',

  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
}));

export const Header = styled(Box, {
  shouldForwardProp: prop => prop !== 'customType',
})<TypeProps>(({theme, customType}) => ({
  width: '100%',
  minHeight: '58px',
  background: getBgColor(customType, theme),
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(4),
}));

export const HeaderTextBox = styled(Box)(({theme}) => ({
  minHeight: '58px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: theme.spacing(1),

  [`${theme.breakpoints.down('sm')}`]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
}));

export const HeaderContent = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  paddingLeft: theme.spacing(4),
}));

export const HeaderIcon = styled(Box, {
  shouldForwardProp: prop => prop !== 'customType',
})<TypeProps>(({theme, customType}) => ({
  display: 'flex',
  alignItems: 'center',
  color: getOnContainerColor(customType, theme),
  '& .material-symbols-outlined': {
    fontSize: '22px',
  },
}));

export const Content = styled(Box)(({theme}) => ({
  background: theme.palette.figma_gray_light_4?.main,
}));

export const HeaderText = styled(Typography)(({theme}) => ({
  color: theme.palette.semantic_light?.primary.onContainer,
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '22px',
}));

export const SubHeaderText = styled(Typography)(({theme}) => ({
  color: theme.palette.figma_primary?.dark,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '16px',
}));

export const CloseButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'customType',
})<TypeProps>(({theme, customType}) => ({
  width: '48px',
  minWidth: 'auto',
  height: '48px',

  '.MuiSvgIcon-root': {
    color: getCloseIconColor(customType, theme),
    fontSize: '20px',
  },
}));
